import { useEffect, useState } from "react";
import { Lock, AlertCircle } from "lucide-react";
import { authPolicy, authStart, claimPassword, forgotPassword, login, setToken, LOGO_URL } from "../lib/api";

export default function Login({ onSuccess, brand }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [policy, setPolicy] = useState(null);
  const [forgot, setForgot] = useState(false);
  const [note, setNote] = useState("");
  const [setup, setSetup] = useState(false);   // an approved person who has no password yet
  const [again, setAgain] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState("");
  useEffect(() => { authPolicy().then(setPolicy).catch(() => {}); }, []);

  const sendReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Enter your email address first"); return; }
    setBusy(true); setError("");
    try { setNote((await forgotPassword(email.trim())).message); } catch { setError("Could not send the reset link. Try again in a minute."); } finally { setBusy(false); }
  };

  const checkEmail = async () => {
    const value = email.trim();
    if (!value.includes("@")) { setSetup(false); setNeedsCode(false); return; }
    try { const r = await authStart(value); setSetup(r.needs_password_setup); setNeedsCode(r.needs_code); } catch { setSetup(false); setNeedsCode(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (forgot) return sendReset(e);
    if (setup) {
      if (needsCode && !code.trim()) { setError("Enter the setup code you were given"); return; }
      if (password.length < 8) { setError("Choose a password of at least 8 characters"); return; }
      if (password !== again) { setError("The two passwords are different"); return; }
      setBusy(true); setError("");
      try { const r = await claimPassword(email.trim(), password, code); setToken(r.token); onSuccess(); }
      catch (err) { const d = err?.response?.data?.detail; setError(typeof d === "string" ? d : "Could not save the password"); } finally { setBusy(false); }
      return;
    }
    if (!password) return;
    setBusy(true); setError("");
    try {
      const { token } = await login(password, email.trim());
      setToken(token);
      onSuccess();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Incorrect email or password");
    } finally { setBusy(false); }
  };

  return (
    <div className="login-shell">
      <form className="modal login-card" onSubmit={submit} data-testid="login-form">
        {brand?.has_logo ? <img src={`${LOGO_URL}?v=${brand.logo_v || 0}`} alt={brand.company_name} className="login-logo" /> : <div className="modal-icon"><Lock size={20} /></div>}
        <h2>Sign in</h2>
        <p>{forgot ? "Enter your email and we will send you a link to choose a new password." : `Sign in to ${brand?.company_name ? `${brand.company_name}'s` : "your"} workspace.`}{policy?.enforced ? ` Use your company email (@${policy.domains[0]}).` : ""}</p>
        <label>Email
          <input
            type="email"
            autoFocus
            autoComplete="username"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setSetup(false); setNeedsCode(false); }}
            onBlur={checkEmail}
            data-testid="login-email-input"
          />
        </label>
        {setup && !forgot && <div className="softphone-ok" data-testid="first-sign-in"><b>Welcome.</b> Your account is ready. Choose a password that only you know (at least 8 characters). You will use it every time you sign in.</div>}
        {setup && needsCode && !forgot && <label>Setup code (from the admin who approved you)
          <input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" placeholder="XXXX-XXXX" data-testid="login-setup-code" />
        </label>}
        {!forgot && <label>{setup ? "Choose a password" : "Password"}
          <input
            type="password"
            autoComplete={setup ? "new-password" : "current-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
          />
        </label>}
        {setup && !forgot && <label>Type it again
          <input type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} data-testid="login-password-again" />
        </label>}
        {note && <div className="softphone-ok" data-testid="forgot-note">{note}</div>}
        {error && <div className="error-strip" data-testid="login-error"><AlertCircle size={14} /> {error}</div>}
        <button className="primary-button full" type="submit" disabled={busy} data-testid="login-submit-button">
          {busy ? (forgot ? "Sending…" : "Please wait…") : (forgot ? "Send me a reset link" : setup ? "Set password and sign in" : "Sign in")}
        </button>
        <button type="button" className="link-button" onClick={() => { setForgot(!forgot); setError(""); setNote(""); }} data-testid="forgot-toggle">{forgot ? "Back to sign in" : "Forgot your password?"}</button>
      </form>
    </div>
  );
}
